import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from './services/api';
import logo from './assets/logo.png';

export default function DocumentVerifyPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialId = searchParams.get('id') || '';
  const [documentId, setDocumentId] = useState(initialId);
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const publicLink = useMemo(() => {
    if (!documentData?.serialNumber) return '';
    return `${window.location.origin}/?documentId=${encodeURIComponent(documentData.serialNumber)}`;
  }, [documentData]);

  const fetchDocument = async (value = documentId) => {
    const serial = String(value || '').trim().toUpperCase();
    if (!serial) {
      setError('اكتب رقم الوثيقة أولاً');
      setDocumentData(null);
      return;
    }

    setLoading(true);
    setError('');
    setDocumentData(null);
    try {
      const res = await api.get(`/documents/${encodeURIComponent(serial)}`);
      setDocumentData(res.data);
      setDocumentId(res.data.serialNumber || serial);
      setSearchParams({ id: res.data.serialNumber || serial });
    } catch (err) {
      setError(err.response?.data?.error || 'لم يتم العثور على وثيقة بهذا الرقم');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (initialId) fetchDocument(initialId);
  }, []);

  return (
    <div dir="rtl" className="min-h-screen bg-slate-950 text-white">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,#1d4ed8_0,transparent_34%),linear-gradient(135deg,#020617_0%,#0f172a_52%,#111827_100%)]" />
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-amber-400/15 to-transparent" />
      </div>

      <main className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-6 sm:px-6 lg:px-8">
        <header className="flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/')}
            className="rounded-lg border border-white/15 bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15"
          >
            الرئيسية
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-amber-300"
          >
            Admin
          </button>
        </header>

        <section className="grid flex-1 items-center gap-8 py-10 lg:grid-cols-[0.95fr_1.05fr]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="space-y-6"
          >
            <img src={logo} alt="شعار المكتبة" className="h-24 w-24 rounded-full border-4 border-amber-300 object-cover shadow-xl" />
            <div className="space-y-3">
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-amber-300">Document Verification</p>
              <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl">
                تأكيد بيانات الوثيقة
              </h1>
              <p className="max-w-xl text-base leading-8 text-slate-200">
                أدخل الرقم التسلسلي للوثيقة لعرض ملف PDF الرسمي مباشرة والتأكد من مطابقته.
              </p>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                fetchDocument();
              }}
              className="rounded-xl border border-white/15 bg-white/10 p-4 shadow-2xl backdrop-blur"
            >
              <label className="mb-2 block text-sm font-semibold text-slate-100">
                رقم الوثيقة
              </label>
              <div className="flex flex-col gap-3 sm:flex-row">
                <input
                  value={documentId}
                  onChange={(e) => setDocumentId(e.target.value)}
                  placeholder="مثال: DOC-20260531-120000-1234"
                  className="min-h-[48px] flex-1 rounded-lg border border-white/15 bg-white px-4 text-left font-mono text-sm text-slate-950 outline-none ring-0 placeholder:text-slate-400 focus:border-amber-300"
                  dir="ltr"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="min-h-[48px] rounded-lg bg-blue-600 px-7 font-bold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  {loading ? 'جاري البحث...' : 'بحث'}
                </button>
              </div>
              {error && (
                <div className="mt-3 rounded-lg border border-red-300/40 bg-red-500/15 px-4 py-3 text-sm text-red-100">
                  {error}
                </div>
              )}
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="min-h-[560px] rounded-xl border border-white/15 bg-white p-3 text-slate-950 shadow-2xl"
          >
            {!documentData && !loading && (
              <div className="flex h-full min-h-[520px] flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 p-6 text-center">
                <div className="mb-4 rounded-full bg-blue-100 px-5 py-4 text-3xl font-black text-blue-700">PDF</div>
                <h2 className="text-xl font-extrabold text-slate-900">الوثيقة ستظهر هنا</h2>
                <p className="mt-2 max-w-md text-sm leading-7 text-slate-600">
                  بعد إدخال الرقم الصحيح، سيتم عرض الملف مباشرة مع رابط الوصول السريع.
                </p>
              </div>
            )}

            {loading && (
              <div className="flex h-full min-h-[520px] items-center justify-center rounded-lg bg-slate-50">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue-200 border-t-blue-700" />
              </div>
            )}

            {documentData && (
              <div className="space-y-3">
                <div className="flex flex-col justify-between gap-3 rounded-lg bg-slate-100 p-4 sm:flex-row sm:items-center">
                  <div>
                    <p className="text-xs font-bold text-slate-500">الرقم التسلسلي</p>
                    <p className="font-mono text-lg font-black text-blue-800" dir="ltr">{documentData.serialNumber}</p>
                    <p className="mt-1 text-sm text-slate-600">{documentData.title || documentData.originalName}</p>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={documentData.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-700"
                    >
                      فتح الملف
                    </a>
                    <button
                      onClick={() => navigator.clipboard?.writeText(publicLink)}
                      className="rounded-lg bg-amber-400 px-4 py-2 text-sm font-bold text-slate-950 hover:bg-amber-300"
                    >
                      نسخ الرابط
                    </button>
                  </div>
                </div>

                <iframe
                  src={documentData.fileUrl}
                  title={documentData.serialNumber}
                  className="h-[650px] w-full rounded-lg border border-slate-200"
                />
              </div>
            )}
          </motion.div>
        </section>
      </main>
    </div>
  );
}
